import { useEffect, useMemo, useState } from 'react';
import { stopTypeRules } from '@views/screens/Quote/components/CreateGetQuote/Tabs/TransportationDetails/utils';
import { Col, Row } from 'antd';
import { t } from 'i18next';

import { Button } from '@nvision/ui';

import { generateStopNumbers } from '@/utilz';
import { AddressSearchModal } from '@/modules/entities';
import { FormSchema } from '@/modules/validators';
import { calculateCube } from '@/utils/measures';
import QuoteStopTypeSelector from '@/views/components/Selectors/QuoteStopTypeSelector';
import { CountrySelect, PortTypeSelect } from '@/views/screens/common';

import FilterCard from '../../../../../../components/Cards/FilterCard';
import FormCheckBox from '../../../../../../components/Inputs/FormCheckBox';
import FormInput from '../../../../../../components/Inputs/FormInput';
import FormSelectBox from '../../../../../../components/Inputs/FormSelectBox';
import HiddenFormInput from '../../../../../../components/Inputs/HiddenFormInput';
import HalPortNameSelector from '../../../../../../components/Selectors/HalPortNameSelector';

const HallTransportationDetailsCard = ({
  index,
  name,
  stopDetails = {},
  handleDataChange,
  form,
  isLast = false,
  totalStops = 0,
  isMultiStopEnabled = true,
  isAddressBookEnabled = false,
}) => {
  const [isAddressSearchOpen, setIsAddressSearchOpen] = useState(false);
  const editMode = Boolean(stopDetails.editMode);

  const cube = useMemo(
    () =>
      calculateCube(
        stopDetails?.length,
        stopDetails?.width,
        stopDetails?.height,
        stopDetails?.billQty,
        stopDetails?.dimUom,
      ),
    [stopDetails?.length, stopDetails?.width, stopDetails?.height, stopDetails?.billQty, stopDetails?.dimUom],
  );

  useEffect(() => {
    if (cube) {
      handleDataChange({ cube }, index);
    }
  }, [cube, handleDataChange, index]);

  useEffect(() => {
    handleDataChange({ isValidLocation: true }, index);
  }, [index, handleDataChange]);

  const handlePortDetails = (data) => {
    handleDataChange(
      {
        countryCode: data?.portCountryCode,
        name: data?.portName,
        portCode: data?.portCode,
        portRecKey: data?.portRecKey,
        portState: data?.portStateCode,
        portType: data?.portType,
        portZip: data?.portZip,
      },
      index,
    );
    if (data?.state) {
      handleDataChange({ isValidLocation: true }, index);
    } else {
      handleDataChange({ isValidLocation: false }, index);
    }
  };

  const handlePortTypeChange = () => {
    handleDataChange(
      {
        name: undefined,
        portCode: undefined,
        portRecKey: undefined,
        portState: undefined,
        portZip: undefined,
      },
      index,
    );
  };

  const handleAddressBookSelection = (address) => {
    handleDataChange(
      {
        addr1: address.address1 || undefined,
        addr2: address.address2 || undefined,
        city: address.city,
        companyName: address.name || undefined,
        countryCode: address.countryCode || undefined,
        isValidLocation: Boolean(address.city && address.countryCode),
        portState: address.stateCode,
        portZip: address.postalCode || undefined,
      },
      index,
    );
  };

  return (
    <FilterCard
      shadow={false}
      showBorder
      className={isLast === false ? 'mb-15' : ''}
      labelForTestId="transportation-details-card"
      customHeader={
        <div className="w-100">
          <Row align="middle" className="w-100" justify="space-between" wrap={false}>
            <Col flex="1 1 auto" style={{ minWidth: 0 }}>
              <Row align="middle" gutter={15}>
                {isMultiStopEnabled && (
                  <Col>
                    <FormCheckBox
                      name={[name, 'selected']}
                      checkBoxClass="normal-checkbox"
                      className="m-0"
                      label={false}
                      dataTestId="transportation-stop-checkbox"
                    />
                  </Col>
                )}

                <Col data-testid="transportation-stop-type" sm={6}>
                  <FormSchema.Field name={`ports.${index}.stopType`}>
                    {({ antdRule, isRequired, label }) => (
                      <QuoteStopTypeSelector
                        dataTestId="transportation-stop-type-selector"
                        autoFocus={editMode}
                        name={[name, 'stopType']}
                        checkBoxClass="normal-checkbox"
                        className="m-0"
                        label={label}
                        singleLineMode
                        colon
                        editMode={isMultiStopEnabled && index !== 0 && editMode}
                        margin="m-0"
                        isRequired={isRequired}
                        rules={[antdRule, ...stopTypeRules(t, form)]}
                        hideDelivery={totalStops === 1}
                      />
                    )}
                  </FormSchema.Field>
                </Col>
                {isMultiStopEnabled && (
                  <Col data-testid="transportation-stop-no" sm={6}>
                    <FormSchema.Field name={`ports.${index}.sequence`}>
                      {({ antdRule, isRequired, label }) => (
                        <FormSelectBox
                          data-testid="transportation-stop-no-selector"
                          label={label}
                          editMode={false}
                          singleLineMode
                          colon
                          name={[name, 'sequence']}
                          placeholder={t('translation:getQuote.transportationDetails.stopNo.placeholder')}
                          margin="m-0"
                          options={generateStopNumbers(totalStops, stopDetails?.stopType)}
                          isRequired={isRequired}
                          rules={[antdRule]}
                        />
                      )}
                    </FormSchema.Field>
                  </Col>
                )}
              </Row>
            </Col>
            {isAddressBookEnabled && (
              <Col className="hall-transportation-details-card__header-actions" flex="none">
                <Button
                  iconLeft="file-search"
                  ghost
                  primary
                  size="sm"
                  type="button"
                  onClick={() => setIsAddressSearchOpen(true)}
                  data-testid="transportation-search-address-button"
                >
                  {t('translation:getQuote.transportationDetails.buttons.searchAddress')}
                </Button>
              </Col>
            )}
          </Row>
        </div>
      }
    >
      <div className="p-15">
        <Row gutter={[16, 16]} align="top" wrap={false}>
          <FormSchema.Field name={`ports.${index}.countryCode`}>
            {({ antdRule, isRequired, label }) => (
              <Col flex={1}>
                <CountrySelect
                  dataTestId="transportation-country-selector"
                  label={label}
                  name={[name, 'countryCode']}
                  editMode={editMode}
                  margin="m-0"
                  isRequired={isRequired}
                  rules={[antdRule]}
                />
              </Col>
            )}
          </FormSchema.Field>
          <FormSchema.Field name={`ports.${index}.city`}>
            {({ antdRule, isRequired, label }) => (
              <Col flex={1}>
                <FormInput
                  name={[name, 'city']}
                  label={label}
                  margin="m-0"
                  editMode={editMode}
                  isRequired={isRequired}
                  rules={[antdRule]}
                  dataTestId="stop-port-city-field"
                />
              </Col>
            )}
          </FormSchema.Field>
          <FormSchema.Field name={`ports.${index}.portType`}>
            {({ antdRule, isRequired, label }) => (
              <Col flex={1}>
                <PortTypeSelect
                  name={[name, 'portType']}
                  label={label}
                  margin="m-0"
                  editMode={editMode}
                  onChange={handlePortTypeChange}
                  isRequired={isRequired}
                  rules={[antdRule]}
                  dependencies={[[name, 'name']]}
                  dataTestId="stop-port-type-selector"
                />
              </Col>
            )}
          </FormSchema.Field>
          <Col flex={1}>
            <HalPortNameSelector
              margin="m-0"
              name={[name, 'name']}
              editMode={editMode}
              portRecKey={stopDetails?.portRecKey}
              portType={stopDetails?.portType}
              countryCode={stopDetails?.countryCode}
              stateCode={stopDetails?.portState}
              zipCode={stopDetails?.portZip}
              portName={stopDetails?.name}
              handlePortDetails={handlePortDetails}
              dependencies={[[name, 'portType']]}
              readOnly
              dataTestId="stop-port-name-selector"
            />
          </Col>
          <FormSchema.Field name="shippingRecords.name">
            {({ antdRule, isRequired, label }) => (
              <Col flex={1}>
                <FormInput
                  name={[name, 'companyName']}
                  label={label}
                  margin="m-0"
                  editMode={editMode}
                  isRequired={isRequired}
                  rules={[antdRule]}
                  disableValidation
                  dataTestId="stop-company-name-field"
                />
              </Col>
            )}
          </FormSchema.Field>
          <FormSchema.Field name={`ports.${index}.addr1`}>
            {({ antdRule, isRequired, label }) => (
              <Col flex={1}>
                <FormInput
                  name={[name, 'addr1']}
                  label={label}
                  margin="m-0"
                  editMode={editMode}
                  isRequired={isRequired}
                  rules={[antdRule]}
                  dataTestId="stop-port-address"
                />
              </Col>
            )}
          </FormSchema.Field>
          <FormSchema.Field name={`ports.${index}.addr2`}>
            {({ antdRule, isRequired, label }) => (
              <Col flex={1}>
                <FormInput
                  name={[name, 'addr2']}
                  label={label}
                  margin="m-0"
                  editMode={editMode}
                  isRequired={isRequired}
                  rules={[antdRule]}
                  dataTestId="stop-port-address-2"
                />
              </Col>
            )}
          </FormSchema.Field>
        </Row>
      </div>
      <HiddenFormInput name={[name, 'editMode']} />
      <HiddenFormInput name={[name, 'isNew']} />
      <HiddenFormInput name={[name, 'gridOperation']} />
      <HiddenFormInput name={[name, 'isValidLocation']} />
      <HiddenFormInput name={[name, 'portRecKey']} />
      <HiddenFormInput name={[name, 'portCode']} />
      <HiddenFormInput name={[name, 'portState']} />
      <HiddenFormInput name={[name, 'portZip']} />
      {isAddressBookEnabled && (
        <AddressSearchModal
          isOpen={isAddressSearchOpen}
          setIsOpen={setIsAddressSearchOpen}
          onSelectionChange={handleAddressBookSelection}
        />
      )}
    </FilterCard>
  );
};

export default HallTransportationDetailsCard;
